import { Component, EventEmitter, Inject, Input, NgZone, OnInit, Output } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { environment } from "../../../../../../../environments/environment";
import { DocumentResult } from "../../../../../../models/document-result";
import { DocumentType } from "../../../../../../models/document-type";
import { Registration } from "../../../../../../models/registration";
import { RequirementControllerService } from "../../../../../../services/requirement-controller/requirement-controller.service";
import { DocumentResultControllerService } from "../../../../../../services/result-controller/document-result-controller.service";
import { NewDeterminantHelperService } from "../../../../../../services/result-controller/new-determinant-helper.service";
import { ResultService } from "../../../../../../services/result-controller/result.service";
import { ServiceControllerService } from "../../../../../../services/service-controller/service-controller.service";
import { Store } from "../../../../../../services/store.service";
import {TranslatedBaseError} from "../../../../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";
import {BotControllerService} from "../../../../../../services/role-controller/bot-controller.service";
import { Bot } from "src/app/models/bot";

@Component({
  selector: "result-edit",
  templateUrl: "./result-edit.component.html",
  styles: [],
  providers: [ToastrService, BotControllerService],
})
export class ResultEditComponent extends TranslatedBaseError implements OnInit {

  // @Input() public result: Requirement;
  @Input() public serviceId: string;
  @Input() public registration: Registration;
  @Input() public documentResult: DocumentResult = new DocumentResult();
  @Input() public addNew: boolean = false;
  @Input() public determinantMapById: { [key: string]: string };
  @Output() public formSubmit = new EventEmitter();
  @Output() public editCancel = new EventEmitter();
  public baseUrl = environment.BASE_URL;
  public showDeterminantBuilder: boolean = false;
  public subscription: any;
  public isLoading: boolean = false;
  public newDocumentType: string;
  public availableDocumentBots: Bot[] = [];

  constructor(public toastr: ToastrService,
              private documentResultControllerService: DocumentResultControllerService,
              protected requirementService: RequirementControllerService,
              protected serviceController: ServiceControllerService,
              private newDeterminantHelperService: NewDeterminantHelperService,
              private resultService: ResultService,
              private botControllerService: BotControllerService,
              @Inject(NgZone) protected zone: NgZone,
              public store: Store, public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnInit() {
    this.subscription = this.resultService.editData.subscribe((data) => {
      this.serviceId = data.serviceId;
      this.registration = data.registration;
      this.documentResult = data.documentResult;
      this.addNew = data.addNew;
      this.showDeterminantBuilder = data.showDeterminantBuilder;

      this.botControllerService.getServiceBots(this.serviceId).subscribe((bots) => {
        this.availableDocumentBots = bots.filter((bot) => bot.botType === "document").map((bot) => {
          const bt = new Bot();
          bt.id = bot.id;
          bt.name = bot.name;
          bt.botType = bot.botType;
          if (bt.id === (this.documentResult.bot && this.documentResult.bot.id)) {
            this.documentResult.bot = bt;
          }
          return bt;
        });
      });
    });
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public handleUpload(fileId: string, item) {
    // if previous exists then lets remove
    if (item.fileId) {
      this.serviceController.deleteServiceFile(this.serviceId, item.fileId).subscribe(
        () => {
          item.fileId = null;
          this.saveFile(fileId, item);
        },
      );
    } else {
      this.saveFile(fileId, item);
    }
  }

  private saveFile(fileId: string, item) {
    if (fileId) {
      if (item.id) {
        item.fileId = fileId;
        this.saveRequirement(item);
      } else {
        this.documentResult.requirement.fileId = fileId;
      }
    }
  }

  public saveRequirement(item) {
    if (!this.isLoading) {
      this.isLoading = true;
      this.requirementService.saveRequirement(item).subscribe(
        () => {
          this.responseSuccessful("Requirement is saved!");
          this.resultService.updateData();
        },
        (error) => this.showErrorMessage(error),
        () => this.isLoading = false,
      );
    }
  }

  public deleteFile(item) {
    let requirement;
    if (item.requirement) {
      requirement = item.requirement;
    } else {
      requirement = item;
    }
    this.serviceController.deleteServiceFile(this.serviceId, requirement.fileId).subscribe(
      () => {
        delete requirement.fileId;
        this.saveRequirement(requirement);
      },
      (error) => this.showErrorMessageWithTitle("File delete error!", error),
    );
  }

  public download(item: any) {
    let requirement: any;
    if (item.requirement) {
      requirement = item.requirement;
    } else {
      requirement = item;
    }
    return this.serviceController.downloadServiceFile(this.serviceId, requirement.fileId).subscribe((res) => {
      const pdfUrl = (window.URL || (window as any).webkitURL).createObjectURL(new Blob([res], { type: res.type }));
      const anchor = document.createElement("a");
      anchor.href = pdfUrl;
      anchor.setAttribute("download", item.fileId);
      anchor.click();
    });
  }

  public createRequirement() {
    if (!this.isLoading) {
      this.isLoading = true;
      const requirement = this.documentResult.requirement;
      this.requirementService.createRequirement(requirement).subscribe(
        (data) => {
          this.documentResult.requirement = data;
          this.responseSuccessful("Requirement is created!");
          this.saveRegistrationResult();
        },
        (error) => {
          this.showErrorMessage(error);
          this.isLoading = false;
        },
      );
    }
  }

  public updateRequirement() {
    if (!this.isLoading) {
      this.isLoading = true;
      this.requirementService.saveRequirement(this.documentResult.requirement).subscribe(
        () => {
          this.saveRegistrationResult();
        },
        (error) => {
          this.isLoading = false;
          this.showErrorMessage(error);
        },
      );
    }
  }

  public saveRegistrationResult() {
    if (this.registration) {
      this.documentResult.registrationId = this.registration.id;
      if (this.documentResult.id) {
        this.documentResult.requirementId = this.documentResult.requirement.id;
        this.documentResultControllerService.saveRegistrationResult(this.documentResult).subscribe(
          () => {
            this.responseSuccessful("Result updated");
            this.resultService.updateData();
            this.formSubmit.emit({ save: true });
          },
          (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
          () => this.isLoading = false,
        );
      } else {
        this.documentResult.requirementId = this.documentResult.id;
        this.documentResultControllerService.addRegistrationResult(this.registration.id, this.documentResult).subscribe(
          () => {
            this.responseSuccessful("Result added!");
            this.resultService.updateData();
            this.formSubmit.emit({ save: true });
          },
          (error) => this.showErrorMessageWithTitle("Error HTTP Post Service!", error),
          () => this.isLoading = false,
        );
      }
    }
    this.newDeterminantHelperService.cleanData();
  }

  public submit() {
    if (this.addNew) {
      this.createRequirement();
    } else {
      this.updateRequirement();
    }
  }

  public closeLayer() {
    this.editCancel.emit({ cancel: true });
  }

  public closeDetLayer() {
    this.store.state.common.determinantSection.reset();
    // this.editCancel.emit({ cancel: true });
  }

  public addDocumentType(newDocumentType: any) {
    if (newDocumentType && newDocumentType.name) {
      const documentType: DocumentType = new DocumentType();
      documentType.type = newDocumentType.name;
      this.documentResult.requirement.types.push(documentType);
    }
    this.newDocumentType = "";
  }

  public deleteDocumentType(documentType: DocumentType) {
    this.documentResult.requirement.types = this.documentResult.requirement.types.filter((obj) => obj.id !== documentType.id);
    }

    public showBotSelection(): boolean {
        return this.documentResult.resultIsDigital;
    }

  appendDeterminant = (det) => {
    let data = JSON.parse(this.documentResult.jsonDeterminants);
    if (!Array.isArray(data)) {
      data = [];
    }
    if (data.length === 0) {
      data.push({
        type: 'OR',
        items: []
      });
    }
    let lastItem = data[data.length - 1];
    if (lastItem.items == null) {
      lastItem = {
        type: 'OR',
        items: []
      };
      data.push(lastItem);
    }
    lastItem.items.push({
      type: det.type || 'OR',
      determinantId: det.determinantId,
    });
    this.documentResult.jsonDeterminants = JSON.stringify(data);
  };
}
